import React, { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getProject } from '../data/projects.js';
import { ArrowLeftIcon, ArrowRightIcon, ExternalIcon, GitHubIcon, PlayIcon } from '../components/Icons.js';
import '../styles/project.css';

// YouTube iframe only loads once the visitor asks for it.
const Demo = ({ youtubeId, title }) => {
    const [playing, setPlaying] = useState(false);
    return (
        <div className="demo-frame">
            {playing ? (
                <iframe
                    src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1`}
                    title={title}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                />
            ) : (
                <button type="button" className="demo-poster" onClick={() => setPlaying(true)}>
                    <img src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`} alt="" />
                    <span className="demo-play">
                        <PlayIcon size={28} />
                    </span>
                    <span className="demo-label">Play demo</span>
                </button>
            )}
        </div>
    );
};

// The hand-written intro under the project title; blank lines separate paragraphs.
// Until one is written, dev builds show a placeholder and production builds show nothing.
const WriteUp = ({ text }) => {
    const paragraphs = (text || '').trim().split(/\n\s*\n/).filter(Boolean);
    if (!paragraphs.length) {
        if (process.env.NODE_ENV === 'production') return null;
        return (
            <p className="writeup-placeholder">
                Write-up goes here. Add it as <code>writeup</code> for this project in src/data/projects.js.
            </p>
        );
    }
    return (
        <div className="case-writeup">
            {paragraphs.map((p) => (
                <p key={p}>{p}</p>
            ))}
        </div>
    );
};

// ---------- Page sections ----------
// Each project lists its own `sections` in src/data/projects.js; `type` picks one of the layouts below.

const SectionHeading = ({ eyebrow, title, body }) => (
    <div className="section-heading">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        {title && <h2 className="case-h2">{title}</h2>}
        {body && <p className="section-body">{body}</p>}
    </div>
);

const Shot = ({ image, alt, caption, portrait }) => (
    <figure className={`shot ${portrait ? 'shot-portrait' : ''}`}>
        <div className="shot-frame">
            <img src={image} alt={alt} loading="lazy" />
        </div>
        {caption && <figcaption>{caption}</figcaption>}
    </figure>
);

const Contrast = ({ title, columns }) => (
    <section className="case-section">
        <div className="container">
            <SectionHeading title={title} />
            <div className="contrast">
                {columns.map((col) => (
                    <div key={col.label} className={`contrast-col card ${col.accent ? 'contrast-accent' : ''}`}>
                        <span className={`pill ${col.accent ? 'pill-accent' : ''}`}>{col.label}</span>
                        <ul>
                            {col.points.map((p) => (
                                <li key={p}>{p}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const Feature = ({ eyebrow, title, body, image, alt, side = 'left' }) => (
    <section className="case-section">
        <div className={`container feature feature-${side}`}>
            <Shot image={image} alt={alt} />
            <SectionHeading eyebrow={eyebrow} title={title} body={body} />
        </div>
    </section>
);

const Wide = ({ eyebrow, title, body, image, alt, caption, narrow }) => (
    <section className="case-section">
        <div className={`container ${narrow ? 'narrow' : ''}`}>
            <SectionHeading eyebrow={eyebrow} title={title} body={body} />
            <Shot image={image} alt={alt} caption={caption} />
        </div>
    </section>
);

const Pair = ({ eyebrow, title, body, items, arrow }) => (
    <section className="case-section">
        <div className="container">
            <SectionHeading eyebrow={eyebrow} title={title} body={body} />
            <div className={`pair ${arrow ? 'pair-arrow' : ''} ${items.some((i) => i.portrait) ? 'pair-has-portrait' : ''}`}>
                <Shot {...items[0]} />
                {arrow && (
                    <span className="pair-connector" aria-hidden="true">
                        <ArrowRightIcon size={22} />
                    </span>
                )}
                <Shot {...items[1]} />
            </div>
        </div>
    </section>
);

const Gallery = ({ eyebrow, title, body, items }) => (
    <section className="case-section">
        <div className="container">
            <SectionHeading eyebrow={eyebrow} title={title} body={body} />
            <div className="gallery">
                {items.map((item) => (
                    <div key={item.caption} className={item.span ? 'gallery-span' : ''}>
                        <Shot {...item} />
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const Stats = ({ title, items }) => (
    <section className="case-section">
        <div className="container">
            <SectionHeading title={title} />
            <dl className="stats">
                {items.map((s) => (
                    <div key={s.label} className="stat">
                        <dt>{s.label}</dt>
                        <dd>{s.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    </section>
);

const Compare = ({ eyebrow, title, body, input, output }) => (
    <section className="case-section">
        <div className="container">
            <SectionHeading eyebrow={eyebrow} title={title} body={body} />
            <div className="compare">
                <figure>
                    <video controls preload="metadata" src={input} />
                    <figcaption>Original input</figcaption>
                </figure>
                <figure>
                    <video controls preload="metadata" src={output} />
                    <figcaption>Processed output</figcaption>
                </figure>
            </div>
        </div>
    </section>
);

const Note = ({ text }) => (
    <section className="case-section">
        <div className="container">
            <p className="case-note">{text}</p>
        </div>
    </section>
);

const SECTIONS = {
    contrast: Contrast,
    feature: Feature,
    wide: Wide,
    pair: Pair,
    gallery: Gallery,
    stats: Stats,
    compare: Compare,
    note: Note,
};

const ProjectPage = () => {
    const { slug } = useParams();
    const project = getProject(slug);
    if (!project) return <Navigate to="/" replace />;

    return (
        <article className="case">
            <header className="case-header">
                <div className="container">
                    <Link to={{ pathname: '/', hash: '#projects' }} className="text-link back-link">
                        <ArrowLeftIcon size={16} /> All projects
                    </Link>
                    <h1>{project.title}</h1>
                    <p className="case-subtitle">{project.subtitle}</p>
                    <WriteUp text={project.writeup} />
                    <ul className="tags">
                        {project.stack.map((t) => (
                            <li key={t}>{t}</li>
                        ))}
                    </ul>
                    <div className="button-row">
                        <a className="button button-primary" href={project.github} target="_blank" rel="noopener noreferrer">
                            <GitHubIcon /> View on GitHub
                        </a>
                        {project.demo && (
                            <a className="button" href="#demo">
                                <PlayIcon size={16} /> Watch demo
                            </a>
                        )}
                    </div>
                </div>
                <div className="container">
                    <figure className="case-hero">
                        <img src={project.hero || project.cover} alt={project.heroAlt || project.coverAlt} />
                    </figure>
                </div>
            </header>

            {project.sections.map((section) => {
                const Section = SECTIONS[section.type];
                return <Section key={section.title || section.text} {...section} />;
            })}

            {project.publications && (
                <section className="case-section">
                    <div className="container narrow">
                        <h2 className="case-h2">Publications</h2>
                        <ul className="pubs">
                            {project.publications.map((pub) => (
                                <li key={pub.title} className="card">
                                    <span className={`pill ${pub.link ? 'pill-accent' : ''}`}>{pub.status}</span>
                                    <h3>{pub.title}</h3>
                                    <p>{pub.description}</p>
                                    {pub.link && (
                                        <a className="text-link" href={pub.link} target="_blank" rel="noopener noreferrer">
                                            Read on {pub.venue} <ExternalIcon size={16} />
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {project.demo && (
                <section id="demo" className="case-section">
                    <div className="container narrow">
                        <h2 className="case-h2">See it in action</h2>
                        <Demo {...project.demo} />
                    </div>
                </section>
            )}

            <section className="case-section">
                <div className="container">
                    <Link to={{ pathname: '/', hash: '#projects' }} className="button">
                        <ArrowLeftIcon size={16} /> Back to projects
                    </Link>
                </div>
            </section>
        </article>
    );
};

export default ProjectPage;
